const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/;

export function dateFunc(date: Date | string | null) {
  if (!date || date === "") {
    return {
      formattedDateTime: "-",
      formattedDateOnly: "-",
    };
  }

  if (typeof date === "string" && date.length <= 10) {
    date = new Date(`${date} `); // to force the date to be in the local time zone
  }

  const dateObject = new Date(date);

  const formattedDateTime = new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
    // already defaults to load string to the local time zone as is
  }).format(dateObject);
  // returns 2025-01-13T12:00:00 => 2025-01-13, 12:00 PM

  const formattedTimeWithSemiColon = new Intl.DateTimeFormat("sv-SE", {
    timeStyle: "short",
  }).format(dateObject);
  // returns 19:10

  const ddmmyyyy = new Intl.DateTimeFormat("en-GB")
    .format(dateObject)
    .replace(/\//g, "");
  // returns: 13/01/2025 (DD/MM/YYYY)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dateString: any = formattedDateTime.substring(
    0,
    dateObject?.getDate() > 9 ? 12 : 11,
  );
  dateString = new Date(dateString);
  dateString = `${dateString?.getDate()?.toString()?.padStart(2, "0")} ${
    monthNames[dateString.getMonth()]
  } ${dateString.getFullYear()}`;
  let timeValue = formattedDateTime.substring(
    formattedDateTime.length,
    formattedDateTime.length - 10,
  );
  timeValue = timeValue.includes("a.m.")
    ? timeValue.replace("a.m.", "am")
    : timeValue.replace("p.m.", "pm");
  return {
    date: dateObject,
    formattedDateTimeWithOutHyphen: ddmmyyyy + " " + formattedTimeWithSemiColon,
    formattedDateTime: `${dateString}, ${timeValue.trimStart()}`,
    formattedDateOnly: dateString,
  };
}

export const formatToLocalYYYYMMDD = (
  date: Date | null | undefined,
): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatToUTCYYYYMMDD = (
  date: Date | null | undefined,
): string | null => {
  if (!date) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const convertUTCDateToLocalDate = (date: Date) => {
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date;
};

export const convertDateStringtoDate = (dateString: string | null) => {
  if (!dateString) return null;
  const dateStringWitoutTime = dateString.split("T")[0]; // Extract the date part (YYYY-MM-DD)
  const [year, month, day] = dateStringWitoutTime.split("-");
  const date = new Date(`${year}-${month}-${day}`);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
};

export const getLocalDateString = (date: Date | null | undefined): string => {
  if (!date) return "";
  const formatter = new Intl.DateTimeFormat("default", { month: "long" });
  const monthName = formatter.format(date);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${day} ${monthName} ${year}`;
};
export const getLocalDateStringYYYYFormat = (
  date: Date | null | undefined,
): string => {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};
export const getLocalDateStringShortMonth = (
  dateString: string | null | undefined,
): string => {
  if (!dateString || dateString === "") return "";
  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat("default", { month: "short" });
  const monthName = formatter.format(date);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${monthName} ${day},  ${year}`;
};

export const getDateStringShortMonth = (
  dateString: string | null | undefined,
): string => {
  if (!dateString || dateString === "") return "";
  const dateWithTimeZone = new Date(dateString);
  const userTimezoneOffset = dateWithTimeZone.getTimezoneOffset() * 60000;
  const date = new Date(dateWithTimeZone.getTime() + userTimezoneOffset);
  const formatter = new Intl.DateTimeFormat("default", { month: "short" });
  const monthName = formatter.format(date);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${monthName} ${day},  ${year}`;
};

export const getDateStringShortMonthTime = (
  dateString: string | null | undefined,
): string => {
  if (!dateString || dateString === "") return "";
  const dateWithTimeZone = new Date(dateString);
  const userTimezoneOffset = dateWithTimeZone.getTimezoneOffset() * 60000;
  const date = new Date(dateWithTimeZone.getTime() + userTimezoneOffset);
  const formatter = new Intl.DateTimeFormat("default", { month: "short" });
  const monthName = formatter.format(date);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${monthName} ${day},  ${year} at ${hours}:${minutes} hrs`;
};

// Serializes a calendar date for APIs without including a time or timezone.
export const toDateOnly = (date: Date | null | undefined): string => {
  if (!date || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Parses the date portion of an API value as a local calendar date for date pickers.
export const parseDateOnly = (
  value: string | null | undefined,
): Date | null => {
  if (!value) return null;

  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return null;

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

// Formats an API calendar date for display without applying timezone conversion.
export const formatDateOnly = (value: string | null | undefined): string => {
  const date = parseDateOnly(value);
  if (!date) return "";

  return `${monthNames[date.getMonth()]} ${String(date.getDate()).padStart(
    2,
    "0",
  )}, ${date.getFullYear()}`;
};

// Formats the start and end of a billing period for display.
export const formatDateOnlyRange = (
  startDate: string,
  endDate: string,
): string => `${formatDateOnly(startDate)} - ${formatDateOnly(endDate)}`;
